import React, { FC } from "react";
import { LuCreditCard, LuLock } from "react-icons/lu";

import { useGetSubscriptionPlanQuery } from "../../slices/SubscriptionSlice";
import {
  formatDay,
  formatMoney,
  intervalWord,
  SubscriptionStatus,
} from "./subscription.utils";
import { useBillingRedirects } from "./useSubscription";

/**
 * The only page a firm sees after the trial + 4 grace days without a card
 * (owner 2026-10-24). Nothing is deleted; everything opens again as soon as a
 * card is added. The server refuses the other API calls too (402).
 */
export const SubscriptionLockPage: FC<{
  status: SubscriptionStatus;
  orgId: string;
  onSignOut: () => void;
}> = ({ status, orgId, onSignOut }) => {
  const { data: plan } = useGetSubscriptionPlanQuery(
    { orgId },
    { skip: !status.canManage },
  );
  const { addPaymentMethod, busy, error } = useBillingRedirects(orgId);
  const contact = status.billingContactName?.trim();
  const endedOn = formatDay(status.trialEndsAt);

  return (
    <div
      className={
        "flex min-h-screen w-full items-center justify-center bg-[#F6F7FB] px-4 py-10 font-Manrope"
      }>
      <div
        role={"main"}
        aria-label={"Add a payment method to continue"}
        className={
          "w-full max-w-[520px] rounded-2xl border border-[#EDEFF3] bg-white px-8 pb-7 pt-9 text-center shadow-[0_24px_60px_rgba(20,22,34,0.10)] max-sm:px-5"
        }>
        <div
          className={
            "mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F0FF] text-[#6B4EFF]"
          }>
          <LuLock size={24} />
        </div>
        <h1 className={"mb-3 text-[22px] font-extrabold text-[#1F2433]"}>
          Add a payment method to continue
        </h1>
        <p className={"mb-2 text-[14px] leading-relaxed text-[#4A5168]"}>
          {endedOn
            ? `Your firm's free trial ended on ${endedOn}.`
            : "Your firm's plan needs a payment method."}{" "}
          All your work, clients and files are safe. Everything opens again as
          soon as a card is added.
        </p>

        {status.canManage ? (
          <>
            {plan && plan.unitAmount > 0 && (
              <div
                className={
                  "mx-auto my-5 flex items-center justify-between rounded-[14px] border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3 text-left"
                }>
                <div>
                  <div
                    className={
                      "text-[12px] font-bold uppercase text-[#8A90A2]"
                    }>
                    Your plan
                  </div>
                  <div className={"text-[14px] font-semibold text-[#1F2433]"}>
                    {Math.max(plan.seats, plan.activePeople)} user
                    {Math.max(plan.seats, plan.activePeople) === 1
                      ? ""
                      : "s"} × {formatMoney(plan.unitAmount, plan.currency)} /{" "}
                    {intervalWord(plan.interval)}
                  </div>
                </div>
                <div className={"text-[18px] font-extrabold text-[#1F2433]"}>
                  {formatMoney(plan.periodTotal, plan.currency)}
                  <span className={"text-[12px] font-semibold text-[#8A90A2]"}>
                    {" "}
                    / {intervalWord(plan.interval)}
                  </span>
                </div>
              </div>
            )}
            <button
              type={"button"}
              disabled={busy}
              onClick={() => void addPaymentMethod()}
              className={
                "mt-2 inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-[10px] bg-[#6B4EFF] text-[15px] font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB] disabled:opacity-60"
              }>
              <LuCreditCard size={18} />
              {busy ? "Opening Stripe…" : "Add payment method"}
            </button>
            <p className={"mt-2 text-[12px] text-[#8A90A2]"}>
              Cards are entered on Stripe&apos;s secure page. ProCharted never
              sees your card number.
            </p>
          </>
        ) : (
          <p
            className={
              "my-5 rounded-[14px] border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3 text-[14px] font-semibold text-[#1F2433]"
            }>
            {contact
              ? `Ask ${contact} (your firm's billing contact) to add a payment method.`
              : "Ask your firm's billing contact to add a payment method."}
          </p>
        )}

        {error && (
          <div
            role={"alert"}
            className={"mt-3 text-[13px] font-bold text-[#B42318]"}>
            {error}
          </div>
        )}

        <button
          type={"button"}
          onClick={onSignOut}
          className={
            "mt-4 h-[40px] w-full rounded-[10px] border border-[#DCE2EA] bg-white text-[14px] font-bold text-[#3A4256] hover:bg-[#F8F9FB]"
          }>
          Sign out
        </button>

        {status.canManage && contact && (
          <p className={"mt-4 text-[12.5px] text-[#8A90A2]"}>
            Not the billing contact? Ask {contact}.
          </p>
        )}
      </div>
    </div>
  );
};
